import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, DatePicker, Row, Select, Tag, Typography } from "antd";
import moment from "moment";
import { useDispatch } from "react-redux";
import EditableTable from "../components/table/EditableTable";
import useWindowSize from "../hooks/useWindowSize";
import { fetchMifirActiveProcess, fetchMifirErrors, fetchMifirErrorTypes, sendNotification } from "../redux/actions";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

// Browser status reads are DB-only, so keep the UI responsive without consuming QuotaGuard.
const MIFIR_STATUS_POLL_INTERVAL_MS = 2000;

const defaultRange = () => [moment().subtract(30, "days").startOf("day"), moment().endOf("day")];

const toDateStrings = range => {
  if (!range || !range[0] || !range[1]) return [null, null];
  return [range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD")];
};

const escapeCsv = value => {
  const text = value === null || typeof value === "undefined" ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const formatUtcDateTime = value => {
  if (!value) return "";
  const parsed = moment.utc(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss [UTC]") : String(value);
};

const statusColor = status => {
  if (status === "SUCCEEDED") return "green";
  if (["FAILED", "UNKNOWN"].includes(status)) return "red";
  if (status === "ABORTED") return "orange";
  return "blue";
};

const ErrorTypeRenderer = ({ value }) => {
  if (value === null || typeof value === "undefined" || value === "") return null;
  const errorType = String(value).trim().toUpperCase();
  const color = errorType === "ERROR" ? "red" : (errorType === "WARNING" ? "orange" : "blue");
  return (
    <Tag className="mifir-error-type-tag" color={color}>
      {errorType}
    </Tag>
  );
};

// Use the same AG Grid table/filter presentation as MiFIR Feedback.
// These columns stay fixed regardless of Error Type filtering.
const ERROR_COLUMNS = [
  {
    headerName: "Identifier ID",
    field: "transaction_id",
    width: 190,
    minWidth: 130,
    cellClass: "mifir-technical-text-cell mifir-center-cell",
    tooltipField: "transaction_id",
  },
  { headerName: "Report Date", field: "report_date", width: 120, minWidth: 95, cellClass: "mifir-center-cell" },
  { headerName: "Report Number", field: "report_number", width: 120, minWidth: 95, cellClass: "mifir-center-cell" },
  { headerName: "Trading Platform", field: "trading_platform", width: 135, minWidth: 105, cellClass: "mifir-center-cell" },
  {
    headerName: "Error Type",
    field: "error_type",
    width: 125,
    minWidth: 100,
    cellClass: "mifir-center-cell",
    cellRendererFramework: ErrorTypeRenderer,
  },
  {
    headerName: "Error Message",
    field: "error_message",
    width: 470,
    minWidth: 280,
    cellClass: "mifir-long-text-cell",
    wrapText: true,
    autoHeight: true,
    tooltipField: "error_message",
  },
  {
    headerName: "Creation Time (UTC)",
    field: "creation_time",
    width: 190,
    minWidth: 155,
    valueFormatter: params => formatUtcDateTime(params.value),
    cellClass: "mifir-center-cell",
  },
].map(column => ({
  editable: false,
  sortable: true,
  filter: true,
  resizable: true,
  ...column,
}));

const MiFIRErrorsPage = () => {
  const dispatch = useDispatch();
  const gridAPI = useRef(null);
  const tableContainerRef = useRef(null);
  const activeProcessRef = useRef(null);
  const loadRef = useRef(null);
  const [range, setRange] = useState(defaultRange());
  const [errorType, setErrorType] = useState("ALL");
  const [errorTypes, setErrorTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [activeProcess, setActiveProcess] = useState(null);
  const { height } = useWindowSize();
  const y = tableContainerRef.current ? tableContainerRef.current.offsetTop : 0;
  const tableHeight = Math.max(360, height - y - 90);

  const loadTypes = useCallback(async (selectedRange = range, preferred = errorType) => {
    const [from, to] = toDateStrings(selectedRange);
    if (!from || !to) return "ALL";
    setLoadingTypes(true);
    try {
      const { payload } = await dispatch(fetchMifirErrorTypes(from, to));
      const values = payload || [];
      setErrorTypes(values);
      const selected = preferred === "ALL" || values.includes(preferred) ? preferred : "ALL";
      setErrorType(selected);
      return selected;
    } catch (err) {
      setErrorTypes([]);
      setErrorType("ALL");
      return "ALL";
    } finally {
      setLoadingTypes(false);
    }
  }, [dispatch, range, errorType]);

  const load = useCallback(async (selectedRange = range, selectedType = errorType) => {
    const [from, to] = toDateStrings(selectedRange);
    if (!from || !to) return;
    setLoading(true);
    if (gridAPI.current) gridAPI.current.showLoadingOverlay();
    try {
      const { payload } = await dispatch(fetchMifirErrors(from, to, selectedType));
      const data = (payload && payload.rows) || [];
      setRows(data.map((row, index) => ({ ...row, _rowId: `error-${index}-${row.transaction_id || ""}-${row.creation_time || ""}` })));
    } catch (err) {
      setRows([]);
      dispatch(sendNotification({
        type: "error",
        message: "MiFIR Errors",
        description: "Could not load MiFIR errors.",
      }));
    } finally {
      setLoading(false);
      if (gridAPI.current) gridAPI.current.hideOverlay();
    }
  }, [dispatch, range, errorType]);

  loadRef.current = load;

  useEffect(() => {
    const initialRange = defaultRange();
    setRange(initialRange);
    Promise.resolve(loadTypes(initialRange, "ALL")).then(selected => load(initialRange, selected));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    let mounted = true;
    let inFlight = false;

    const poll = async () => {
      if (!mounted || inFlight) return;
      inFlight = true;
      try {
        const { payload } = await dispatch(fetchMifirActiveProcess());
        if (!mounted) return;
        const wasActive = Boolean(activeProcessRef.current);
        const nowActive = Boolean(payload);
        activeProcessRef.current = payload || null;
        setActiveProcess(payload || null);

        if (wasActive && !nowActive && loadRef.current) {
          await loadRef.current();
        }
      } catch (err) {
        // Live process status is best-effort; the next poll retries.
      } finally {
        inFlight = false;
      }
    };

    let timer = null;

    const scheduleNextPoll = () => {
      if (!mounted) return;
      timer = setTimeout(async () => {
        await poll();
        scheduleNextPoll();
      }, MIFIR_STATUS_POLL_INTERVAL_MS);
    };

    Promise.resolve(poll()).finally(scheduleNextPoll);
    const onFocus = () => poll();
    const onVisibility = () => {
      if (document.visibilityState === "visible") poll();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      mounted = false;
      clearTimeout(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dispatch]);

  const onRangeChange = async value => {
    if (!value || !value[0] || !value[1]) return;
    setRange(value);
    const selected = await loadTypes(value, errorType);
    await load(value, selected);
  };

  const onTypeChange = async value => {
    const selected = value || "ALL";
    setErrorType(selected);
    await load(range, selected);
  };

  const exportCSV = () => {
    if (!rows.length) return;
    const headers = ["Identifier ID", "Report Date", "Report Number", "Trading Platform", "Error Type", "Error Message", "Creation Time (UTC)"];
    const keys = ["transaction_id", "report_date", "report_number", "trading_platform", "error_type", "error_message", "creation_time"];
    const csv = [
      headers.map(escapeCsv).join(","),
      ...rows.map(row => keys.map(key => escapeCsv(key === "creation_time" ? formatUtcDateTime(row[key]) : row[key])).join(",")),
    ].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const [from, to] = toDateStrings(range);
    link.href = url;
    link.setAttribute("download", `mifir-errors-${from}-${to}${errorType !== "ALL" ? `-${errorType}` : ""}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columnDefs = useMemo(() => ERROR_COLUMNS, []);

  return (
    <div className="mifir-operational-page">
      <div className="mifir-page-heading">
        <div>
          <h2>MiFIR Errors</h2>
          <Text type="secondary">Review report-generation errors for your MiFIR transactions.</Text>
        </div>
      </div>

      {activeProcess && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={(
            <span>
              MiFIR process running: <strong>{activeProcess.is_repreparation ? "REPREPARATION" : activeProcess.process_type}</strong>{" "}
              <Tag color={statusColor(activeProcess.status)}>
                {activeProcess.status === "SUCCEEDED" ? "SUCCESSFUL" : activeProcess.status}
              </Tag>
            </span>
          )}
          description={[
            activeProcess.report_date ? `Report date: ${String(activeProcess.report_date).substring(0, 10)}` : null,
            activeProcess.trading_platform ? `Platform: ${activeProcess.trading_platform}` : null,
            activeProcess.run_id ? `Run ID: ${activeProcess.run_id}` : null,
            activeProcess.progress_percent !== null && typeof activeProcess.progress_percent !== "undefined" && Number.isFinite(Number(activeProcess.progress_percent)) ? `Progress: ${Number(activeProcess.progress_percent)}%` : null,
            activeProcess.message || null,
          ].filter(Boolean).join(" | ")}
        />
      )}

      <div className="mifir-toolbar">
        <Row className="mifir-toolbar-row" gutter={[10, 6]} align="middle">
          <Col>
            <div className="mifir-filter-field">
              <Text className="mifir-filter-label">Report Date Range</Text>
              <RangePicker
                className="mifir-filter-range"
                value={range}
                onChange={onRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </div>
          </Col>
          <Col>
            <div className="mifir-filter-field">
              <Text className="mifir-filter-label">Error Type</Text>
              <Select
                className="mifir-filter-select mifir-filter-select--wide"
                value={errorType}
                onChange={onTypeChange}
                loading={loadingTypes}
              >
                <Option value="ALL">All</Option>
                {errorTypes.map(value => <Option key={value} value={value}>{value}</Option>)}
              </Select>
            </div>
          </Col>
          <Col>
            <div className="mifir-toolbar-actions">
              <Button type="primary" onClick={exportCSV} disabled={!rows.length || loading}>Export CSV</Button>
            </div>
          </Col>
        </Row>
      </div>

      <div ref={tableContainerRef} className="mifir-grid-card mifir-grid-card--fit mifir-grid-card--errors">
        <EditableTable
          columnDefs={columnDefs}
          defaultColDefs={{ sortable: true, filter: true, resizable: true }}
          rowData={rows}
          setAPI={api => { gridAPI.current = api; }}
          getRowNodeId={data => data._rowId}
          height={tableHeight}
          headerHeight={52}
          rowHeight={34}
          fitColumnsToWidth
          suppressHorizontalScroll
        />
      </div>
    </div>
  );
};

export default MiFIRErrorsPage;
